import { ChevronDown } from 'lucide-react';
import type { CSSProperties, FC } from 'react';

import type { StorefrontMenuItem } from '@/features/storefront/api/storefront';
import { StorefrontContainer } from '@/features/storefront/components/StorefrontContainer';
import {
  HeaderBannerRail,
  HeaderCategoryLinks,
  MenuLink,
  MobileNav,
  StoreLogo,
} from '@/features/storefront/layouts/chrome/ChromeParts';
import { MegaColumns, useHoverDisclosure } from '@/features/storefront/layouts/chrome/MegaMenu';
import { menuItems, type HeaderProps } from '@/features/storefront/layouts/chrome/types';
import { cn } from '@/lib/cn';

/** The masthead's navigation voice: the rounded heading face, medium weight. */
const TUMBLE_NAV = 'items-center gap-7 whitespace-nowrap text-[16px] font-medium';

const NAV_FONT: CSSProperties = { fontFamily: 'var(--storefront-heading-font)' };

/** A nav link: a soft yellow underline marks the one under the pointer. */
const NAV_LINK =
  'border-b-2 border-transparent pb-0.5 hover:border-[color:var(--storefront-accent)]';

/**
 * The rail's ground: the sunshine accent rather than the text colour, with the
 * ink as its copy - by the same variable swap the other families make, and
 * with the copy swapped too, because the rail's default copy is the cream page
 * colour and cream on yellow does not read.
 */
const RAIL_STYLE = {
  '--storefront-palette-text': 'var(--storefront-accent)',
  '--storefront-palette-background': 'var(--storefront-text)',
} as CSSProperties;

/**
 * The violet deck, painted in the merchant's primary with the page colour as
 * its ink.
 *
 * Every shared piece of the corner - search, account, cart, the menu button -
 * paints itself in `--storefront-primary`, and a violet link on a violet deck
 * is an invisible one. So the deck redeclares the names those pieces read:
 * "primary" and "text" become the cream, "background" becomes the violet. The
 * dropdowns the corner opens (the account menu, the language list, the phone's
 * menu sheet) paint with the same names, so they come out violet with cream
 * copy rather than cream on cream. The `palette-*` names are never redeclared,
 * which is what lets the deck - and the mega panel below - still reach the
 * theme's real colours.
 */
const DECK_STYLE = {
  '--storefront-primary': 'var(--storefront-palette-background)',
  '--storefront-text': 'var(--storefront-palette-background)',
  '--storefront-muted': 'color-mix(in srgb, var(--storefront-palette-background) 75%, transparent)',
  '--storefront-background': 'var(--storefront-palette-primary)',
  '--storefront-surface':
    'color-mix(in srgb, var(--storefront-palette-background) 22%, var(--storefront-palette-primary))',
  backgroundColor: 'var(--storefront-palette-primary)',
  color: 'var(--storefront-palette-background)',
} as CSSProperties;

/**
 * The mega panel hangs from the violet deck but is drawn on the page's own
 * cream, so it puts the theme's real colours back for everything inside it.
 */
const PANEL_STYLE = {
  '--storefront-primary': 'var(--storefront-palette-primary)',
  '--storefront-text': 'var(--storefront-palette-text)',
  '--storefront-muted': 'var(--storefront-palette-muted)',
  '--storefront-background': 'var(--storefront-palette-background)',
  '--storefront-surface': 'var(--storefront-palette-surface)',
  backgroundColor: 'var(--storefront-palette-background)',
  color: 'var(--storefront-palette-text)',
} as CSSProperties;

/**
 * The wordmark's own primary: the sunshine accent. A shop with no logo gets a
 * badge painted in the primary with a white bag on it, and on the deck the
 * primary is the cream - a white bag on a cream badge is no badge at all.
 */
const MARK_STYLE = {
  '--storefront-primary': 'var(--storefront-accent)',
} as CSSProperties;

/** Whether an item opens a whole panel of columns rather than a short list. */
function isMega(item: StorefrontMenuItem): boolean {
  return (item.items ?? []).some((child) => (child.items ?? []).length > 0);
}

/**
 * One item on the bar, and whatever the merchant filed under it.
 *
 * An item whose children have children - "Shop" → Play & learn → Soft toys -
 * opens a **mega panel** the width of the page, one column per child with its
 * links under a rounded heading, on the cream page colour with its bottom
 * corners rounded like a toy box lid. An item with a flat list of children
 * opens a small card under itself. Either way the item stays a link. Opens on
 * hover and on focus, closes on Escape and when focus leaves.
 */
function NavItem({ item, preview }: { item: StorefrontMenuItem; preview?: boolean }) {
  const { open, props } = useHoverDisclosure();
  const children = item.items ?? [];
  const mega = isMega(item);

  if (children.length === 0) {
    return <MenuLink item={item} preview={preview} className={NAV_LINK} />;
  }

  return (
    <div
      className={cn('flex h-full items-center', !mega && 'relative')}
      {...props}
    >
      <span className="flex items-center gap-1">
        <MenuLink item={item} preview={preview} className={NAV_LINK} />
        <ChevronDown
          className={cn('size-4 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </span>

      {open && mega ? (
        // Positioned against the header (the nearest positioned ancestor), so
        // it spans the page under the deck rather than hanging off the item.
        <div className="absolute inset-x-0 top-full z-40 px-3 sm:px-5">
          <div
            className="rounded-b-[2rem] border border-t-0 shadow-xl"
            style={{ ...PANEL_STYLE, borderColor: 'var(--storefront-palette-surface)' }}
          >
            <StorefrontContainer className="py-10">
              <MegaColumns
                linksClassName="gap-3 text-[15px] font-normal"
                columns={children.map((column) => ({
                  key: `${column.label}:${column.url}`,
                  heading: (
                    <MenuLink
                      item={column}
                      preview={preview}
                      className="mb-4 block text-xl font-semibold leading-tight hover:opacity-70"
                    />
                  ),
                  links: (column.items ?? []).map((link) => (
                    <MenuLink
                      key={`${link.label}:${link.url}`}
                      item={link}
                      preview={preview}
                      className="hover:text-[color:var(--storefront-primary)]"
                    />
                  )),
                }))}
              />
            </StorefrontContainer>
          </div>
        </div>
      ) : null}

      {open && !mega ? (
        <div className="absolute left-1/2 top-full z-40 -translate-x-1/2 pt-2">
          <div
            className="min-w-52 rounded-2xl border py-2 shadow-xl"
            style={{ ...PANEL_STYLE, borderColor: 'var(--storefront-palette-surface)' }}
          >
            {children.map((child) => (
              <MenuLink
                key={`${child.label}:${child.url}`}
                item={child}
                preview={preview}
                className="block px-5 py-2 hover:text-[color:var(--storefront-primary)]"
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * A toy shop's masthead: the yellow rail with the shop's standing promises,
 * then one violet deck - the menu on the left in the rounded face, the
 * wordmark in the middle, the shopper's corner on the right.
 *
 * Like the other families, the bar stands the shop's own shelves in when the
 * merchant has built no menu, and below the large breakpoint the menu moves
 * into the sheet behind the menu button.
 */
export const Header: FC<HeaderProps> = ({
  storeName,
  logoUrl,
  description,
  header,
  menu,
  endSlot,
  preview,
}) => {
  const items = menuItems(menu);

  return (
    <header
      className={cn('relative', header.sticky && !preview && 'sticky top-0 z-40')}
      style={{ backgroundColor: 'var(--storefront-palette-primary)' }}
    >
      <div style={RAIL_STYLE}>
        <HeaderBannerRail header={header} fallback={description} preview={preview} />
      </div>

      <div style={DECK_STYLE}>
        {/* Three columns on a wide screen, the outer two equal so the
            wordmark sits dead centre. On a phone the corner needs the room
            more than the symmetry does, so the wordmark moves up beside the
            menu button and the corner takes what is left. */}
        <StorefrontContainer className="grid min-h-[4.25rem] grid-cols-[auto_minmax(0,1fr)_auto] items-stretch gap-2 sm:gap-6 lg:min-h-[4.75rem] lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
          <div className="flex min-w-0 items-center" style={NAV_FONT}>
            <MobileNav menu={menu} hiddenFrom="lg:hidden" preview={preview} categoryFallback />
            {/* The column's whole room, so the category strip below measures
                what it has rather than what it holds. */}
            <div className="hidden h-full min-w-0 flex-1 lg:flex preview-narrow:hidden!">
              {items.length > 0 ? (
                <nav aria-label={menu?.name} className={cn('flex', TUMBLE_NAV)}>
                  {items.map((item) => (
                    <NavItem key={`${item.label}:${item.url}`} item={item} preview={preview} />
                  ))}
                </nav>
              ) : preview ? null : (
                <HeaderCategoryLinks
                  className={cn('flex', TUMBLE_NAV)}
                  linkClassName={NAV_LINK}
                  header={header}
                  limit={5}
                />
              )}
            </div>
          </div>

          <div className="flex min-w-0 items-center justify-start lg:justify-center" style={MARK_STYLE}>
            <StoreLogo
              storeName={storeName}
              logoUrl={logoUrl}
              logoWidthPx={header.logo_width_px}
              showStoreName={header.show_store_name}
              preview={preview}
              className="gap-2.5"
              nameClassName="text-[1.5rem] sm:text-[2.1rem] font-bold leading-none"
              nameStyle={{ fontFamily: 'var(--storefront-heading-font)' }}
            />
          </div>

          <div className="flex min-w-0 items-center justify-end gap-3 sm:gap-5">{endSlot}</div>
        </StorefrontContainer>
      </div>
    </header>
  );
};
